import React, { useState } from 'react';
import Modal from 'antd/lib/modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import AsyncButton from '../AsyncButton';
import { useCreateOrg, useValidateOrgUrl } from '../../../services/hooks/useOrg';
import { faSpinnerThird } from '@fortawesome/pro-solid-svg-icons';
import { faUsers } from '@fortawesome/pro-light-svg-icons';

const OrgCreateSchema = Yup.object().shape({
  orgInput: Yup.string().label('Organization Name').required(),
});

export interface Props {
  visible: boolean;
  closable: boolean;
  onCancel: any;
}

const CreateOrgModal: React.FC<Props> = ({ visible, closable, onCancel }) => {
  const [showValidMessage, setShowValidMessage] = useState(false);
  const { mutate, isPending, isError } = useCreateOrg();
  const { mutateAsync: validateUrl, isPending: validatingUrl } = useValidateOrgUrl();
  const createOrgState = isPending ? 'REQUEST' : isError ? 'FAILURE' : '';

  const handleSubmit = ({ orgInput, urlInput }: any) => {
    mutate({ name: orgInput, url: urlInput });
  };

  const handleValidateUrl = (value: string) => {
    return new Promise<void | string>(async (resolve, reject) => {
      setShowValidMessage(false);
      if (!value) return reject('Organization URL is a required field');

      const match = value.match(/^[a-z0-9_]*$/g);
      if (!match) {
        return reject('Only a-z, 0-9 and _ are allowed.');
      }

      const payload = await validateUrl(value);
      if (payload) {
        const { suggestedUrl } = payload;
        // we are OK, url does not exist
        if (suggestedUrl === value) {
          setShowValidMessage(true);
          return resolve();
        }
        if (!suggestedUrl) {
          return resolve(`Only a-z, 0-9 and _ are allowed.`);
        }
        return resolve(`That URL is already in use. Suggested: "${suggestedUrl}"`);
      }
    });
  };

  return (
    <Modal
      open={visible}
      title={
        <span className="flex-container align-center">
          <FontAwesomeIcon icon={faUsers} size="2x" className="margin-right-md" />
          {!closable && 'Lets get started! Create an organization.'}
          {closable && 'Create a new organization.'}
        </span>
      }
      footer={null}
      closable={closable}
      onCancel={onCancel}
      maskClosable={closable}
    >
      <div className="form-container padding-hsides-sm padding-bottom-sm">
        <Formik
          initialValues={{ orgInput: '', urlInput: '' }}
          validateOnChange={false}
          validateOnBlur={false}
          validationSchema={OrgCreateSchema}
          onSubmit={handleSubmit}
          render={() => (
            <Form>
              <div className="form-group">
                <label htmlFor="orgInput">Organization Name</label>
                <div className="input-group">
                  <Field name="orgInput" className="form-control" />
                </div>
                <ErrorMessage name="orgInput">{(msg: string) => <div className="invalid-feedback">{msg}</div>}</ErrorMessage>
              </div>
              <div className="form-group">
                <label htmlFor="urlInput">
                  <span className="margin-right-xs">Organization URL</span>
                  {validatingUrl ? <FontAwesomeIcon icon={faSpinnerThird} size="lg" className="fa-fast-spin" /> : ''}
                </label>
                <div className="input-group">
                  <div className="input-group-prepend">
                    <div className="input-group-text">my.gigg.com/</div>
                  </div>
                  <Field name="urlInput" className="form-control" validate={handleValidateUrl} />
                </div>
                {!isPending ? (
                  <ErrorMessage name="urlInput">
                    {(msg: string) => <div className="invalid-feedback">{msg}</div>}
                  </ErrorMessage>
                ) : (
                  ''
                )}
                {showValidMessage ? <div className="valid-feedback">URL is available!</div> : ''}
              </div>
              <div className="margin-top-md flex-container justify-end">
                {closable && (
                  <button type="button" className="btn margin-right-xs" onClick={onCancel}>
                    Cancel
                  </button>
                )}
                <AsyncButton type="submit" className="btn btn-primary" asyncState={createOrgState}>
                  Create Organization
                </AsyncButton>
              </div>
            </Form>
          )}
        />
      </div>
    </Modal>
  );
};

export default CreateOrgModal;
